import i18n from '@/i18n';
import { notificationService } from '@/services/notification/notification-service';
import { runWithConcurrency } from './concurrencyPool';
import type { FlattenedUploadEntry } from './uploadTreeFlattening';

// Bounded concurrency, not unlimited Promise.all and not fully sequential - avoids Graph
// throttling (429s) on large folder trees while staying much faster than one-at-a-time.
const DEFAULT_UPLOAD_CONCURRENCY = 4;

export type UploadItemStatus = 'queued' | 'uploading' | 'done' | 'failed';

export interface UploadFolderTreeDeps {
  createFolder: (parentPath: string, name: string) => Promise<unknown>;
  uploadFile: (parentPath: string, file: File) => Promise<unknown>;
  concurrency?: number;
}

export interface UploadFolderTreeResult {
  succeeded: number;
  failed: number;
}

const dirname = (relativePath: string): string | null => {
  const index = relativePath.lastIndexOf('/');
  return index === -1 ? null : relativePath.slice(0, index);
};

const basename = (relativePath: string): string => {
  const index = relativePath.lastIndexOf('/');
  return index === -1 ? relativePath : relativePath.slice(index + 1);
};

const joinPath = (basePath: string, segment: string): string =>
  basePath === '/' ? `/${segment}` : `${basePath}/${segment}`;

// Every directory this tree needs, deduplicated and sorted shallowest-first so a parent is always
// created before any of its children.
const collectDirectories = (entries: FlattenedUploadEntry[]): string[] => {
  const dirs = new Set<string>();
  for (const entry of entries) {
    let dir = dirname(entry.relativePath);
    while (dir !== null) {
      dirs.add(dir);
      dir = dirname(dir);
    }
  }
  return Array.from(dirs).sort((a, b) => a.split('/').length - b.split('/').length);
};

/**
 * Turns a flattened `{relativePath, file}` list (from either drag-drop or a `webkitdirectory`
 * picker - see uploadTreeFlattening.ts) into created folders + uploaded files under `basePath`.
 *
 * Best-effort per branch: if a specific subfolder's creation genuinely fails, only that branch's
 * descendants (subfolders and files) are marked failed - unrelated siblings and the rest of the
 * tree keep going. Folder-creation conflicts (409/already-exists) are already resolved as success
 * by the injected `createFolder`, so a rejection here always means a real error.
 */
export async function uploadFolderTree(
  basePath: string,
  entries: FlattenedUploadEntry[],
  deps: UploadFolderTreeDeps,
  onStatusChange?: (relativePath: string, status: UploadItemStatus) => void,
): Promise<UploadFolderTreeResult> {
  for (const entry of entries) onStatusChange?.(entry.relativePath, 'queued');

  const directories = collectDirectories(entries);
  const failedDirs = new Set<string>();

  const isUnderFailedDir = (relDir: string | null): boolean => {
    if (relDir === null) return false;
    for (const failedDir of failedDirs) {
      if (relDir === failedDir || relDir.startsWith(`${failedDir}/`)) return true;
    }
    return false;
  };

  for (const dir of directories) {
    const parentRelDir = dirname(dir);
    if (isUnderFailedDir(parentRelDir)) {
      failedDirs.add(dir);
      continue;
    }
    const parentFullPath = parentRelDir === null ? basePath : joinPath(basePath, parentRelDir);
    try {
      await deps.createFolder(parentFullPath, basename(dir));
    } catch {
      failedDirs.add(dir);
    }
  }

  let succeeded = 0;
  let failed = 0;

  await runWithConcurrency(entries, deps.concurrency ?? DEFAULT_UPLOAD_CONCURRENCY, async (entry) => {
    const relDir = dirname(entry.relativePath);
    if (isUnderFailedDir(relDir)) {
      failed += 1;
      onStatusChange?.(entry.relativePath, 'failed');
      return;
    }

    onStatusChange?.(entry.relativePath, 'uploading');
    const parentFullPath = relDir === null ? basePath : joinPath(basePath, relDir);
    try {
      await deps.uploadFile(parentFullPath, entry.file);
      succeeded += 1;
      onStatusChange?.(entry.relativePath, 'done');
    } catch {
      failed += 1;
      onStatusChange?.(entry.relativePath, 'failed');
    }
  });

  return { succeeded, failed };
}

// Shared by every caller that drives a batch (useFileManagerUpload's runBatch,
// useNativeFileDropTarget's onDrop) - the upload mutation itself no longer toasts per file, so
// each batch-driving call site surfaces one summary toast instead, to avoid toast-spam on large
// folder trees.
export function notifyUploadBatchResult({ succeeded, failed }: UploadFolderTreeResult): void {
  const { t } = i18n.global;
  notificationService.open({
    text: failed > 0 ? t('fileManager.upload.uploadFolderTree.summaryWithFailures', { succeeded, failed }) : t('fileManager.upload.uploadFolderTree.summary', { succeeded }),
    type: failed === 0 ? 'success' : succeeded > 0 ? 'warning' : 'danger',
  });
}

// Only one batch runs at a time (uploadItems is replaced wholesale per batch, so a second
// concurrent batch would overwrite the first one's progress panel). Every entry point that could
// start one while another is in flight refuses through this shared notifier rather than returning
// silently, which is how a dropped folder or a picked file used to vanish with no explanation.
// Shared, for the same reason notifyUploadBatchResult is: the message is not restated per caller.
export function notifyUploadBusy(): void {
  const { t } = i18n.global;
  notificationService.open({
    text: t('fileManager.upload.uploadFolderTree.busy'),
    type: 'warning',
  });
}
